import React, { useState, useEffect } from 'react';
import { Button, Input, message } from 'antd';
import { CodeBlock } from './CodeBlock';
import styles from './styles.module.css';
import cx from 'classnames';
import * as translations from '../strings';
import { getLangKey } from 'appJS/models/Language';
import { post, put } from 'appJS/utils/fetch';
import { SquareCheckBig } from 'lucide-react';
import { emailRegex } from 'appJS/components/invite';

const strings = translations[getLangKey()];

export const JavasvcriptContent = ({
  onInstallCallback,
  project: { apiKey, id: projectId, javascriptInstalledOverride },
  bugherdUrl,
  isOnboarding
}: {
  onInstallCallback: (isWordpress?: boolean) => void;
  project: {
    apiKey: string;
    id: string;
    javascriptInstalledOverride: boolean;
  };
  bugherdUrl: string;
  isOnboarding: boolean;
}) => {
  const [showSendInstructions, setShowSendInstructions] = useState<boolean>(
    false
  );
  const [email, setEmail] = useState<string>('');
  const [instructionsLoading, setInstructionsLoading] = useState<boolean>(
    false
  );
  const [installedLoading, setInstalledLoading] = useState<boolean>(false);
  const projectUrl = `${bugherdUrl}/projects/${projectId}`;

  const sendInstructions = async () => {
    setInstructionsLoading(true);
    try {
      const request = await post(`${projectUrl}/send_help`, { email });
      const response = await request;
      if (response) message.success(`${strings.deliveredTo}${email}`);
    } catch (err) {
      message.error(strings.tryAgain);
    } finally {
      setInstructionsLoading(false);
    }
  };

  const setJavascriptInstalled = async event => {
    const optionClick = event.metaKey || event.ctrlKey;
    if (javascriptInstalledOverride && !optionClick) return;

    setInstalledLoading(true);
    try {
      const request = await put(projectUrl, {
        javascript_installed_override: !javascriptInstalledOverride
      });
      const response = await request;
      if (response) {
        message.success(strings.updatedProject(!javascriptInstalledOverride));
      }
    } catch (err) {
      message.error(strings.tryAgain);
      setInstalledLoading(false);
    } finally {
      onInstallCallback();
    }
  };

  useEffect(() => {
    setInstalledLoading(false);
  }, [javascriptInstalledOverride]);

  const validEmail = emailRegex.test(email);
  let title: string | undefined;
  let icon: React.ReactNode | undefined;
  let displayKey: string = 'doneThis';

  if (javascriptInstalledOverride) {
    title = strings.overrideTitle;
    icon = <SquareCheckBig className={styles.checkIcon} />;
    displayKey = 'installed';
  }

  return (
    <div className={styles.javascriptCode}>
      <CodeBlock apiKey={apiKey} bugherdUrl={bugherdUrl} />
      <div
        className={cx(styles.actions, {
          [styles.showInstructions]: showSendInstructions
        })}
      >
        {!showSendInstructions && (
          <Button
            type="primary"
            onClick={setJavascriptInstalled}
            className={cx(styles.actionButton, {
              [styles.overridden]:
                !installedLoading && javascriptInstalledOverride
            })}
            title={title}
            icon={icon}
            loading={installedLoading}
          >
            {installedLoading ? strings.updating : strings[displayKey]}
          </Button>
        )}
        {!isOnboarding && (
          <Button
            type="text"
            onClick={() => setShowSendInstructions(true)}
            className={cx({ [styles.staticButtonState]: showSendInstructions })}
          >
            {strings.sendInstructions}
          </Button>
        )}
        {showSendInstructions && (
          <div className={styles.sendInstructions}>
            <Input
              className={styles.instructionInput}
              placeholder={strings.enterEmail}
              value={email}
              type="email"
              onChange={e => setEmail(e.target.value)}
            />
            <Button
              type="primary"
              onClick={sendInstructions}
              className={styles.actionButton}
              disabled={!email || !validEmail}
              loading={instructionsLoading}
            >
              {strings.send}
            </Button>
            {email && !validEmail && (
              <span className={styles.invalidEmail}>
                {strings.invalidEmail}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
